import { apply, burst, celebrate, give, onceToday, take, toast } from '../core/effects';
import { S } from '../core/game';
import { GIFT_ITEMS, type AffectionKey, type ItemKey } from '../core/state';
import { m } from '../i18n';
import { itemInfo, npcName, objName } from '../i18n/labels';
import { sfx } from '../audio/sfx';
import { say, type Choice } from '../ui/dialog';
import { h } from '../ui/dom';
import { ITEM_ICONS } from '../core/effects';
import { openShop } from '../ui/inventory';
import { openPanel, refreshPanel, closePanel } from '../ui/panel';
import { openEnglishHere } from '../ui/english';
import { openMinigame } from '../minigames/registry';
import type { ObjectId } from '../world/mapdef';
import { sayEnglish } from '../audio/speech';
import { logOrder } from './passport';
import { buyWithQuestion } from './lifeMoney';
import { WATER_MAX } from '../core/state';

const min = (n: number) => m().common.minutes(n);
const already = () => toast(m().common.alreadyToday);

/** Riding state, read by the world (speed, paddock limits) and the renderer (horse under you). */
export type Ride = 'horse' | 'pony' | 'bike' | 'kick' | 'ebike';
export const riding = { on: null as null | Ride };
/** Speed relative to walking. */
export const RIDE_SPEED: Record<Ride, number> = { horse: 1.7, pony: 1.5, bike: 1.7, kick: 1.5, ebike: 2.1 };
export const isVehicle = (r: Ride | null): r is 'bike' | 'kick' | 'ebike' => r === 'bike' || r === 'kick' || r === 'ebike';

export const PLACE_OBJECTS: Partial<Record<ObjectId, () => void>> = {
  nurseDesk: nurseVisit,
  pool() {
    const t = m().places.pool;
    say({
      who: objName('pool'),
      icon: '🏊',
      text: t.text,
      choices: [
        {
          text: t.swim,
          cost: min(30),
          run: () => {
            if (S().stats.energy < 25) return toast(t.tired);
            sfx('water');
            burst('drop', 14);
            apply({ energy: -15, mood: 12, minutes: 30, stars: onceToday('swim') ? 4 : 0 });
            toast(m().places.pool.swimDone);
          },
        },
        { text: t.rules, run: () => say({ who: objName('pool'), icon: '📋', text: m().places.pool.rulesText }) },
        { text: `🗣️ English`, run: openEnglishHere },
      ],
    });
  },
  gymNet: () => openMinigame('volleyball'),
  volleyNet: () => openMinigame('volleyball'),
  tennisNet: () => openMinigame('tennis'),
  fountain: wish,
  mallFountain: wish,
  cafeCounter: () => openShop('cafe'),
  giftCounter: () => openShop('gift'),
  deptCounter: openOutfits,
  restCounter: openRestaurant,
  life_fastfood: openFastFood,
};

function wish(): void {
  const t = m().places.fountain;
  say({
    who: objName('fountain'),
    icon: '⛲',
    text: t.text,
    choices: [
      {
        text: t.wish,
        run: () => {
          if (S().coins < 1) return toast(t.noCoin);
          apply({ coins: -1 }, { float: false });
          sfx('pickup');
          burst('sparkle', 10);
          apply({ mood: onceToday('wish') ? 8 : 1 });
          toast(m().places.fountain.wishDone);
        },
      },
    ],
  });
}

function nurseVisit(): void {
  const t = m().places.nurse;
  say({
    who: npcName('nurse'),
    icon: '🩺',
    text: t.text,
    choices: [
      {
        text: t.checkup,
        cost: min(10),
        run: () => {
          if (!onceToday('checkup')) return say({ who: npcName('nurse'), icon: '🩺', text: m().places.nurse.already });
          sfx('ok');
          apply({ energy: 25, mood: 5, minutes: 10 });
          say({ who: npcName('nurse'), icon: '🩺', text: m().places.nurse.checkupDone });
        },
      },
      { text: t.bandage, run: () => say({ who: npcName('nurse'), icon: '🩹', text: m().places.nurse.bandageText }) },
      { text: `🗣️ English`, run: openEnglishHere },
    ],
  });
}

// --- Outfits (department store) -------------------------------------------------------------

/** Colour sets for the player's clothes; index 0 is the school uniform. */
export const OUTFITS: readonly { top: string; topShade: string; bottom: string }[] = [
  { top: '#f4f1ea', topShade: '#cfc8bb', bottom: '#2f4a8a' },
  { top: '#7fc8f0', topShade: '#4f9fd0', bottom: '#2f3442' },
  { top: '#ff9fc0', topShade: '#e07a9c', bottom: '#ff7aa0' },
  { top: '#e2493b', topShade: '#b83a2e', bottom: '#f4f1ea' },
  { top: '#57a868', topShade: '#3f8a4f', bottom: '#5b4636' },
];
const OUTFIT_PRICE = 80;

function openOutfits(): void {
  openPanel('outfits', () => {
    const s = S();
    const p = m().places;
    const owned = (i: number) => i === 0 || !!s.forever[`outfit.${i}`];
    return {
      title: p.shops.dept.title,
      body: [
        h('p', { class: 'hint' }, p.shops.dept.hint),
        h('div', { class: 'wallet' }, h('span', null, `🪙 ${s.coins}`)),
        OUTFITS.map((o, i) =>
          h(
            'div',
            { class: 'item-row' },
            h('span', { class: 'swatch', style: `background:linear-gradient(180deg, ${o.top} 55%, ${o.bottom} 55%)` }),
            h('div', { class: 'item-info' }, h('b', null, p.outfits[i]), h('small', null, (s.player.outfit ?? 0) === i ? '✅' : owned(i) ? '' : `${OUTFIT_PRICE}🪙`)),
            h(
              'button',
              {
                class: 'btn small gold',
                disabled: (s.player.outfit ?? 0) === i || (!owned(i) && s.coins < OUTFIT_PRICE),
                onclick: () => {
                  if (!owned(i)) {
                    apply({ coins: -OUTFIT_PRICE }, { float: false });
                    s.forever[`outfit.${i}`] = true;
                  }
                  s.player.outfit = i;
                  celebrate();
                  toast(p.outfitOn(p.outfits[i]));
                  refreshPanel('outfits');
                },
              },
              owned(i) ? '👕' : '🛒',
            ),
          ),
        ),
      ],
    };
  });
}

// --- Restaurant -------------------------------------------------------------------------------------

const DISHES: { price: number; energy: number; mood: number; study: number }[] = [
  { price: 40, energy: 35, mood: 4, study: 0 },
  { price: 35, energy: 30, mood: 5, study: 0 },
  { price: 30, energy: 28, mood: 2, study: 3 },
];

function openRestaurant(): void {
  openPanel('restaurant', () => {
    const s = S();
    const t = m().places.restaurant;
    return {
      title: t.title,
      body: [
        h('p', { class: 'hint' }, t.hint),
        h('div', { class: 'wallet' }, h('span', null, `🪙 ${s.coins}`)),
        DISHES.map((d, i) =>
          h(
            'div',
            { class: 'item-row' },
            h('span', { class: 'item-icon' }, ['🍜', '🥟', '🍚'][i]),
            h('div', { class: 'item-info' }, h('b', null, t.dishes[i].name), h('small', null, t.dishes[i].desc)),
            h(
              'button',
              {
                class: 'btn small gold',
                disabled: s.coins < d.price,
                onclick: () => {
                  if (s.daily.dinnerOut) return toast(m().places.restaurant.full);
                  s.daily.dinnerOut = true;
                  apply({ coins: -d.price, energy: d.energy, mood: d.mood, study: d.study, minutes: 30 });
                  sfx('ok');
                  toast(m().places.restaurant.eaten(t.dishes[i].name));
                  closePanel('restaurant');
                },
              },
              `${d.price}🪙`,
            ),
          ),
        ),
        h('button', { class: 'btn small blue wide', onclick: () => (closePanel('restaurant'), openEnglishHere()) }, '🗣️ English'),
      ],
    };
  });
}

// --- Fast food (life package): order in English, compare combo prices, pick the healthy one ------------

const FF: { price: number; energy: number; mood: number; bladder?: number; kind: 'need' | 'want'; icon: string; split?: { need: number; want: number }; healthy?: boolean }[] = [
  { price: 45, energy: 25, mood: 5, kind: 'need', icon: '🍔' },
  { price: 25, energy: 10, mood: 6, kind: 'want', icon: '🍟' },
  { price: 20, energy: 4, mood: 4, bladder: 8, kind: 'want', icon: '🥤' },
  { price: 75, energy: 39, mood: 12, bladder: 8, kind: 'want', icon: '🍱', split: { need: 45, want: 30 } },
  { price: 50, energy: 28, mood: 5, kind: 'need', icon: '🌯', healthy: true },
];
const FF_SUM = FF[0].price + FF[1].price + FF[2].price;

/** Polite and rude ways to order (English practice). */
const POLITE = [(x: string) => `Can I have ${x}, please?`, (x: string) => `I’d like ${x}, please.`, (x: string) => `Could I get ${x}, please?`];
const RUDE = [(x: string) => `Give me ${x}.`, (x: string) => `I want ${x}. Hurry up!`, (x: string) => `${x[0].toUpperCase()}${x.slice(1)}. Now.`];
const pickOne = <T,>(a: readonly T[]): T => a[Math.floor(Math.random() * a.length)];

function openFastFood(): void {
  openPanel('lifeFastFood', () => {
    const s = S();
    const t = m().life.ff;
    return {
      title: t.title,
      className: 'life-ff',
      body: [
        h('p', { class: 'hint' }, t.hint),
        h('div', { class: 'wallet' }, h('span', null, `🪙 ${s.coins}`)),
        FF.map((f, i) =>
          h(
            'div',
            { class: `item-row${f.healthy ? ' life-ff-healthy' : ''}` },
            h('span', { class: 'item-icon' }, f.icon),
            h('div', { class: 'item-info' }, h('b', null, t.menu[i].name), h('small', null, t.menu[i].desc)),
            h('button', { class: 'btn small gold', disabled: s.coins < f.price, onclick: () => (closePanel('lifeFastFood'), ffOrder(i)) }, `${f.price}🪙`),
          ),
        ),
        h('div', { class: 'life-ff-compare' }, h('b', null, t.compare), h('p', null, rich2(t.compareText(FF_SUM, FF[3].price)))),
        h('button', { class: 'btn small blue wide', onclick: () => (closePanel('lifeFastFood'), ffWater()) }, `💧 ${t.water}`),
      ],
    };
  });
}

/** `**bold**` for a panel paragraph. */
function rich2(text: string): (string | HTMLElement)[] {
  return text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((p) => (p.startsWith('**') ? h('b', null, p.slice(2, -2)) : p));
}

function ffOrder(i: number): void {
  const t = m().life.ff;
  const f = FF[i];
  const item = t.menu[i];
  const ok = pickOne(POLITE)(item.en);
  const bad = [pickOne(RUDE)(item.en), RUDE.map((r) => r(item.en)).find((x) => x !== ok) ?? RUDE[0](item.en)].filter((x, k, a) => a.indexOf(x) === k);
  sayEnglish(t.greet, 0.9);
  say({
    who: t.cashier,
    icon: '🍔',
    text: `“${t.greet}”\n\n${t.prompt}`,
    en: t.greet,
    choices: [
      ...[ok, ...bad]
        .sort(() => Math.random() - 0.5)
        .map(
          (o): Choice => ({
            text: `🗣️ ${o}`,
            run: () => {
              sayEnglish(o, 0.9);
              if (o !== ok) {
                sfx('bad');
                return say({ who: t.cashier, icon: '🍔', text: pickOne(t.wrong), choices: [{ text: m().common.back, run: () => ffOrder(i) }] });
              }
              if (S().coins < f.price) return toast(t.poor);
              buyWithQuestion({ label: item.name, kind: f.kind, price: f.price, who: t.cashier, split: f.split }, () => {
                burst('sparkle', 6);
                apply({ energy: f.energy, mood: f.mood, bladder: f.bladder, minutes: 15 });
                logOrder();
                if (onceToday('order.life_fastfood')) apply({ stars: 2 }, { float: false });
                const extra = f.healthy && onceToday('life.ffHealthy') ? (apply({ stars: 2 }, { float: false }), `\n\n🥗 ${t.healthy}`) : i === 3 ? `\n\n${t.tooMuch}` : '';
                sayEnglish(t.done, 0.9);
                say({ who: t.cashier, icon: f.icon, text: `“${t.done}”${extra}`, en: t.done });
              });
            },
          }),
        ),
      { text: t.look },
    ],
  });
}

function ffWater(): void {
  const t = m().life.ff;
  const ok = 'Can I have some water, please?';
  sayEnglish(ok, 0.85);
  say({
    who: t.cashier,
    icon: '💧',
    text: `🗣️ “${ok}”`,
    en: ok,
    choices: [
      {
        text: `🗣️ ${ok}`,
        run: () => {
          sayEnglish(t.waterDone, 0.9);
          if (S().bag.water < WATER_MAX) give('water', 1);
          sfx('water');
          say({ who: t.cashier, icon: '💧', text: `“${t.waterDone}”`, en: t.waterDone });
        },
      },
      { text: m().common.back, run: openFastFood },
    ],
  });
}

// --- Gifts ------------------------------------------------------------------------------------------

const LOVE_OF: Partial<Record<string, AffectionKey>> = { mom: 'mom', dad: 'dad', sister: 'sister', emma: 'emma', chunbo: 'chunbo', xiaoyu: 'xiaoyu', xiaohua: 'xiaohua', xiaomei: 'xiaomei', azhe: 'azhe', xiaoqiang: 'xiaoqiang', xiaofang: 'xiaofang' };

export function giftChoice(npcId: string): Choice | null {
  const s = S();
  const have = GIFT_ITEMS.filter((k) => s.bag[k] > 0);
  if (!have.length) return null;
  return {
    text: m().places.gifts.give,
    run: () =>
      say({
        who: npcName(npcId),
        icon: '🎁',
        text: m().places.gifts.pick,
        choices: have.map((k: ItemKey) => ({
          text: `${ITEM_ICONS[k]} ${itemInfo(k).name}`,
          run: () => {
            if (!take(k)) return;
            const first = onceToday(`gift.${npcId}`);
            const love = LOVE_OF[npcId];
            celebrate();
            burst('heart', 8);
            apply({ mood: 5, stars: first ? 3 : 0, love: love ? { [love]: first ? 12 : 3 } : undefined });
            const lines = m().places.gifts.thanks;
            say({ who: npcName(npcId), icon: '💝', text: lines[Math.floor(Math.random() * lines.length)] });
          },
        })),
      }),
  };
}

// --- People at the new places -------------------------------------------------------------------------

export function talkPlaces(id: string): boolean {
  switch (id) {
    case 'nurse':
      nurseVisit();
      return true;
    case 'lifeguard':
      say({ who: npcName('lifeguard'), icon: '🛟', text: m().places.pool.rulesText, choices: [{ text: '🗣️ English', run: openEnglishHere }] });
      return true;
    case 'barista':
      say({ who: npcName('barista'), icon: '☕', text: m().places.shops.cafe.hint, choices: [{ text: '🛒', run: () => openShop('cafe') }, { text: '🗣️ English', run: openEnglishHere }] });
      return true;
    case 'giftClerk':
      say({ who: npcName('giftClerk'), icon: '🎁', text: m().places.shops.gift.hint, choices: [{ text: '🛒', run: () => openShop('gift') }, { text: '🗣️ English', run: openEnglishHere }] });
      return true;
    case 'deptClerk':
      say({ who: npcName('deptClerk'), icon: '👕', text: m().places.shops.dept.hint, choices: [{ text: '👕', run: openOutfits }, { text: '🗣️ English', run: openEnglishHere }] });
      return true;
    case 'waiter':
      say({ who: npcName('waiter'), icon: '🍜', text: m().places.restaurant.hint, choices: [{ text: '🍽️', run: openRestaurant }, { text: '🗣️ English', run: openEnglishHere }] });
      return true;
    case 'musicTeacher':
      say({ who: npcName('musicTeacher'), icon: '🎼', text: m().piano.text, choices: [{ text: '🎹', run: () => openMinigame('rhythm') }, { text: '🗣️ English', run: openEnglishHere }] });
      return true;
    case 'stableKeeper':
      say({ who: npcName('stableKeeper'), icon: '🤠', text: m().places.horse.text, choices: [{ text: '🗣️ English', run: openEnglishHere }] });
      return true;
    case 'horse':
    case 'pony':
      horse(id);
      return true;
  }
  return false;
}

function horse(kind: 'horse' | 'pony'): void {
  const t = m().places.horse;
  say({
    who: npcName(kind),
    icon: '🐴',
    text: t.text,
    choices: [
      riding.on
        ? { text: t.dismount, run: () => ((riding.on = null), sfx('pop')) }
        : {
            text: t.ride,
            run: () => {
              riding.on = kind;
              sfx('fanfare');
              if (onceToday('ride')) apply({ mood: 15, stars: 4 });
              toast(m().places.horse.riding);
            },
          },
      {
        text: t.feed,
        run: () => {
          if (!onceToday(`feed.${kind}`)) return already();
          sfx('pop');
          burst('heart', 6);
          apply({ mood: 6 });
          toast(m().places.horse.fed);
        },
      },
    ],
  });
}

export { give };
